import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, Plus, Upload, Image, Loader2, Check, Sparkles, ExternalLink, X } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { useData } from '../context/DataContext';

const QuickSave = () => {
  const { createAd, uploadAd, pasteAd } = useData();
  const [inputValue, setInputValue] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [pasteDialog, setPasteDialog] = useState({ open: false, imageData: null, sourceUrl: '' });
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  const handleQuickSave = async () => {
    if (!inputValue.trim()) return;
    
    setIsLoading(true);
    try {
      await createAd({ source_url: inputValue.trim() });
      setShowSuccess(true);
      setInputValue('');
      toast.success('Ad saved to Inbox!', {
        icon: <Sparkles className="w-4 h-4 text-violet-400" />
      });
      setTimeout(() => setShowSuccess(false), 2000);
    } catch (err) {
      toast.error('Failed to save ad');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleQuickSave();
    }
  };

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setIsLoading(true);
    try {
      for (const file of files) {
        if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
          await uploadAd(file);
          toast.success(`${file.name} saved!`, {
            icon: <Sparkles className="w-4 h-4 text-violet-400" />
          });
        }
      }
    } catch (err) {
      toast.error('Failed to upload file');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    handleFileUpload(files);
  }, []);

  const handlePaste = useCallback(async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const blob = item.getAsFile();
        if (blob) {
          try {
            const reader = new FileReader();
            reader.onload = (event) => {
              // Show dialog to optionally add source URL
              setPasteDialog({
                open: true,
                imageData: event.target.result,
                sourceUrl: ''
              });
            };
            reader.readAsDataURL(blob);
          } catch (err) {
            toast.error('Failed to read image');
          }
        }
        break;
      }
    }
  }, []);

  const handlePasteSubmit = async (withUrl = false) => {
    setIsLoading(true);
    try {
      await pasteAd(pasteDialog.imageData, null, withUrl ? pasteDialog.sourceUrl : null);
      toast.success('Screenshot saved!', {
        icon: <Sparkles className="w-4 h-4 text-violet-400" />
      });
      setPasteDialog({ open: false, imageData: null, sourceUrl: '' });
    } catch (err) {
      toast.error('Failed to save screenshot');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full"
    >
      <div
        ref={dropZoneRef}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onPaste={handlePaste}
        className={`
          relative rounded-2xl p-1 transition-all duration-300
          ${isDragging ? 'bg-violet-500/20 ring-2 ring-violet-500' : 'bg-zinc-800/50'}
          quick-save-input
        `}
      >
        <div className="flex items-center gap-2 p-2">
          <div className="flex-1 relative">
            <Link className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
            <Input
              data-testid="quick-save-input"
              type="text"
              placeholder="Paste Instagram URL or drop image..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              className="
                pl-12 pr-4 h-14 text-lg
                bg-transparent border-0 
                focus:ring-0 focus-visible:ring-0
                placeholder:text-zinc-600
              "
            />
          </div>
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            className="hidden"
            onChange={(e) => handleFileUpload(Array.from(e.target.files))}
          />
          
          <Button
            data-testid="upload-button"
            variant="ghost"
            size="icon"
            className="h-12 w-12 text-zinc-400 hover:text-white hover:bg-zinc-700"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="w-5 h-5" />
          </Button>
          
          <AnimatePresence mode="wait">
            {isLoading ? (
              <motion.div
                key="loading"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
              >
                <Button
                  disabled
                  className="h-12 px-6 bg-violet-600"
                >
                  <Loader2 className="w-5 h-5 animate-spin" />
                </Button>
              </motion.div>
            ) : showSuccess ? (
              <motion.div
                key="success"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
              >
                <Button
                  className="h-12 px-6 bg-emerald-600"
                >
                  <Check className="w-5 h-5" />
                </Button>
              </motion.div>
            ) : (
              <motion.div
                key="save"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
              >
                <Button
                  data-testid="save-button"
                  onClick={handleQuickSave}
                  disabled={!inputValue.trim()}
                  className="
                    h-12 px-6 
                    bg-violet-600 hover:bg-violet-500 
                    disabled:bg-zinc-700 disabled:text-zinc-500
                    shadow-lg shadow-violet-500/20
                  "
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Save
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        
        {/* Drag overlay */}
        <AnimatePresence>
          {isDragging && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="
                absolute inset-0 rounded-2xl
                bg-violet-500/10 border-2 border-dashed border-violet-500
                flex items-center justify-center
                pointer-events-none
              "
            >
              <div className="text-center">
                <Image className="w-12 h-12 mx-auto mb-2 text-violet-400" />
                <p className="text-violet-300 font-medium">Drop to save</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Helper text */}
      <div className="flex items-center justify-center gap-6 mt-4 text-sm text-zinc-500">
        <span className="flex items-center gap-1.5">
          <Link className="w-4 h-4" /> Paste URL
        </span>
        <span className="flex items-center gap-1.5">
          <Image className="w-4 h-4" /> Drop image
        </span>
        <span className="flex items-center gap-1.5">
          <kbd className="px-1.5 py-0.5 rounded bg-zinc-800 text-xs">⌘V</kbd> Paste screenshot
        </span>
      </div>

      {/* Paste Screenshot Dialog */}
      <Dialog open={pasteDialog.open} onOpenChange={(open) => !isLoading && setPasteDialog({ ...pasteDialog, open })}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Image className="w-5 h-5 text-violet-400" />
              Save Screenshot
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Optionally add the original Instagram URL for reference
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Preview */}
            {pasteDialog.imageData && (
              <div className="relative rounded-lg overflow-hidden bg-zinc-800 max-h-48">
                <img 
                  src={pasteDialog.imageData} 
                  alt="Screenshot preview" 
                  className="w-full h-auto max-h-48 object-contain"
                />
              </div>
            )}
            
            {/* Source URL Input */}
            <div>
              <Label className="text-zinc-400 text-sm">Original Instagram URL (optional)</Label>
              <div className="relative mt-1.5">
                <ExternalLink className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  data-testid="paste-source-url"
                  type="url"
                  placeholder="https://www.instagram.com/p/..."
                  value={pasteDialog.sourceUrl}
                  onChange={(e) => setPasteDialog({ ...pasteDialog, sourceUrl: e.target.value })}
                  className="pl-10 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-600"
                />
              </div>
              <p className="text-xs text-zinc-500 mt-1">
                This helps viewers find the original post on Instagram
              </p>
            </div>
            
            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setPasteDialog({ open: false, imageData: null, sourceUrl: '' })}
                disabled={isLoading}
                className="flex-1 text-zinc-400 hover:text-white"
              >
                Cancel
              </Button>
              <Button
                data-testid="save-screenshot-btn"
                onClick={() => handlePasteSubmit(!!pasteDialog.sourceUrl)}
                disabled={isLoading}
                className="flex-1 bg-violet-600 hover:bg-violet-500"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default QuickSave;
